/**
 * 1.3.1 media portal, stream Q4: the flow editor's part of the workflow steps Assign for delivery
 * (assign_for_delivery) and Wait for delivery (repeat_until with check delivery_accepted). Step 0 wired every export
 * into the editor's shared files:
 *   PortalPaletteItems    Builder.tsx's palette (after the built-in steps): "Assign for delivery" and "Wait for
 *                         delivery" (a repeat_until with check delivery_accepted and the condition nodes.<id>.output.response.state equals accepted)
 *   portalStepDefaults    Builder.tsx's defaults() for assign_for_delivery
 *   AssignForDeliveryEditor   NodeInspector.tsx's form for assign_for_delivery
 *   DeliveryCheckFields   NodeInspector.tsx's Repeat until form when its check is delivery_accepted
 *   describePortalStep    GraphCanvas.tsx's one-line summary of both
 * What the forms pick from: GET /api/v1/assignments/choices (portal.assign; useAssignmentChoices in
 * api/hooks/assignments.ts, read only). Q4's file.
 */
import { useEffect, useMemo, useState } from 'react';
import { PackageCheck, Plus, Trash2, UserCheck } from 'lucide-react';
import {
  ITEM_KIND_PRESET, ITEM_KINDS, ITEM_TYPE_PRESETS, NODE_TYPES,
  type AutomationGraph, type AutomationNode, type DeliveryItemTemplate, type ItemKind, type NodeType,
} from '@prism/contracts';
import { useAssignmentChoices } from '../../api/hooks/assignments';
import { useGroupTypes } from '../../api/hooks/groups';
import { browserZone, timeZones } from '../assignments/zoned';

type AssignNode = Extract<AutomationNode, { type: 'assign_for_delivery' }>;
type RepeatNode = Extract<AutomationNode, { type: 'repeat_until' }>;
type ExtraKind = Exclude<ItemKind, 'media'>;
const EXTRA_KINDS = ITEM_KINDS.filter((k): k is ExtraKind => k !== 'media');
const KIND_LABEL: Record<ExtraKind, string> = { document: 'Document', subtitles: 'Subtitles', image: 'Image', audio: 'Audio', other: 'Other' };
const NONE = '00000000-0000-4000-8000-000000000000';
const typesOf = (s: string) => [...new Set(s.split(/[\s,;]+/).map((x) => x.trim().replace(/^\./, '').toLowerCase()).filter(Boolean))];
const typesText = (t: readonly string[]) => (t.length ? t.map((x) => `.${x}`).join(' ') : 'any type');

/** The step's icon (GraphCanvas' ICONS). */
export const assignForDeliveryIcon = <UserCheck size={14} />;

/** The condition the palette's Wait for delivery starts with. */
export const acceptedCondition = (id: string) => ({ path: `nodes.${id}.output.response.state`, op: 'equals' as const, value: 'accepted' });

/** Builder.tsx: a palette entry adds a node of `type` with these fields (given the new node's id) over the type's defaults. */
export function PortalPaletteItems({ onAdd }: { onAdd: (type: NodeType, fields?: (id: string) => Record<string, unknown>) => void }) {
  return (
    <>
      <p className="section-label" style={{ marginTop: 12 }}>Media portal</p>
      <button className="palette-item cat-people" onClick={() => onAdd('assign_for_delivery')} title={NODE_TYPES.assign_for_delivery.description} data-testid="add-assign_for_delivery">
        {assignForDeliveryIcon}<span>{NODE_TYPES.assign_for_delivery.label}</span>
      </button>
      <button className="palette-item cat-logic" data-testid="add-wait_for_delivery"
        title="Wait until the media portal delivery of the asset is accepted in Intake; checks again every hour for up to 14 days"
        onClick={() => onAdd('repeat_until', (id) => ({
          label: 'Wait for delivery', everySeconds: 3600, maxAttempts: 336, check: { type: 'delivery_accepted' }, all: [acceptedCondition(id)],
        }))}>
        <PackageCheck size={14} /><span>Wait for delivery</span>
      </button>
    </>
  );
}

/** Builder.tsx: the new assign_for_delivery node's fields (the schema's defaults need an assignee). */
export function portalStepDefaults(): Record<string, unknown> {
  return { assignee: { type: 'company', companyId: NONE }, specId: null, due: { type: 'after_run', days: 7, hours: 0, timeZone: browserZone() }, items: [], notes: '', ifOpen: 'fail' };
}

/** One extra item: the label and types are kept locally while typed, so an empty label or a trailing comma is not saved. */
function ItemRow({ item, n, onChange, onRemove }: { item: DeliveryItemTemplate; n: number; onChange: (i: DeliveryItemTemplate) => void; onRemove: () => void }) {
  const [label, setLabel] = useState(item.label);
  const [types, setTypes] = useState(item.acceptedTypes.join(', '));
  useEffect(() => { setTypes(item.acceptedTypes.join(', ')); }, [item.acceptedTypes]);
  return (
    <div className="assign-item" data-testid="flow-item">
      <div className="assign-item-row">
        <select className="select" aria-label="Kind" value={item.kind} data-testid="flow-item-kind"
          onChange={(e) => { const kind = e.target.value as ExtraKind; onChange({ ...item, kind, acceptedTypes: [...ITEM_TYPE_PRESETS[ITEM_KIND_PRESET[kind]]] }); }}>
          {EXTRA_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <input className="input" aria-label="Label" placeholder="Label, e.g. Final manuscript" value={label} maxLength={200} aria-invalid={!label.trim()} data-testid="flow-item-label"
          onChange={(e) => { setLabel(e.target.value); if (e.target.value.trim()) onChange({ ...item, label: e.target.value }); }} />
        <label className="assign-required"><input type="checkbox" checked={item.required} onChange={(e) => onChange({ ...item, required: e.target.checked })} /> Required</label>
        <button type="button" className="btn ghost icon sm" aria-label={`Remove item ${n + 1}`} onClick={onRemove}><Trash2 size={13} /></button>
      </div>
      <div className="assign-item-row">
        <input className="input" aria-label="Short description" placeholder="Short description, e.g. PDF, signed off by the director" value={item.description} maxLength={2000}
          onChange={(e) => onChange({ ...item, description: e.target.value })} />
        <input className="input assign-types" aria-label="Accepted types" placeholder="Any type" value={types} title="File extensions, separated by commas; empty: any type"
          onChange={(e) => setTypes(e.target.value)} onBlur={() => onChange({ ...item, acceptedTypes: typesOf(types) })} />
      </div>
    </div>
  );
}

/** NodeInspector.tsx: the form of an assign_for_delivery node. */
export function AssignForDeliveryEditor({ node, set }: { node: AssignNode; set: (patch: Partial<AutomationNode>) => void }) {
  const choices = useAssignmentChoices();
  const companies = choices.data?.companies ?? [];
  const specs = choices.data?.specs ?? [];
  const companyOfUser = (uid: string) => companies.find((c) => c.users.some((u) => u.id === uid));
  const companyId = node.assignee.type === 'company' ? node.assignee.companyId : companyOfUser(node.assignee.portalUserId)?.id ?? NONE;
  const company = companies.find((c) => c.id === companyId);
  const spec = specs.find((s) => s.id === node.specId);
  const due = node.due;
  const zone = due.type === 'after_run' ? ((due as { timeZone?: string }).timeZone || 'UTC') : 'UTC';
  const zones = useMemo(() => timeZones(zone), [zone]);
  const patch = (p: Partial<AssignNode>) => set(p as Partial<AutomationNode>);
  // 1.4.0 (G2): "the run's group" (scope group), optionally of one group type.
  const groupScope = node.scope === 'group';
  const groupTypes = useGroupTypes(groupScope).data?.items.filter((t) => !t.archived) ?? [];

  if (choices.isError) {
    return <p className="help" data-testid="flow-assign-forbidden">Choosing a company needs the <b>Assign deliveries</b> permission (portal.assign). Ask an administrator, or someone who has it, to set this step up.</p>;
  }
  return (
    <>
      <span className="help">{groupScope
        ? 'Asks a production company to deliver every placeholder of the run’s group (the group the run’s asset is in, and its sub-groups) through the media portal. Members with media or an open assignment are skipped. The supplier gets the usual assignment email for each.'
        : 'Asks a production company to deliver the run’s asset through the media portal. The asset must be a placeholder (no media yet); an asset with media fails the step. The supplier gets the usual assignment email.'}</span>
      <div className="field-row">
        <div className="field"><label htmlFor="n-afd-scope">Assign</label>
          <select id="n-afd-scope" className="select" value={groupScope ? 'group' : 'asset'} data-testid="flow-assign-scope"
            onChange={(e) => patch(e.target.value === 'group' ? { scope: 'group', groupTypeId: null } : { scope: undefined, groupTypeId: undefined })}>
            <option value="asset">The run’s asset</option>
            <option value="group">The run’s group</option>
          </select></div>
        {groupScope && (
          <div className="field"><label htmlFor="n-afd-group-type">Group</label>
            <select id="n-afd-group-type" className="select" value={node.groupTypeId ?? ''} data-testid="flow-assign-group-type"
              onChange={(e) => patch({ groupTypeId: e.target.value || null })}>
              <option value="">Its lowest group (e.g. the Season)</option>
              {groupTypes.map((t) => <option key={t.id} value={t.id}>Its {t.label}</option>)}
            </select></div>
        )}
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor="n-afd-company">Company</label>
          <select id="n-afd-company" className="select" value={companyId} data-testid="flow-assign-company"
            onChange={(e) => patch({ assignee: { type: 'company', companyId: e.target.value } })}>
            <option value={NONE}>{choices.isLoading ? 'Loading…' : 'Choose…'}</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div className="field"><label htmlFor="n-afd-user">Who</label>
          <select id="n-afd-user" className="select" disabled={!company} data-testid="flow-assign-user"
            value={node.assignee.type === 'user' ? node.assignee.portalUserId : ''}
            onChange={(e) => patch({ assignee: e.target.value ? { type: 'user', portalUserId: e.target.value } : { type: 'company', companyId } })}>
            <option value="">Every user of the company</option>
            {company?.users.map((u) => <option key={u.id} value={u.id}>{u.name}{u.invited ? ' (invited)' : ''}</option>)}
          </select></div>
      </div>
      <div className="field"><label htmlFor="n-afd-spec">Delivery spec</label>
        <select id="n-afd-spec" className="select" value={node.specId ?? ''} data-testid="flow-assign-spec" onChange={(e) => patch({ specId: e.target.value || null })}>
          <option value="">None (any media file)</option>
          {specs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <span className="help">{spec ? `${spec.description || spec.name}: ${typesText(spec.acceptedTypes)}.` : 'The house format the media is checked against.'}</span>
      </div>
      <div className="field"><label htmlFor="n-afd-due">Due</label>
        <select id="n-afd-due" className="select" value={due.type} data-testid="flow-assign-due"
          onChange={(e) => patch({ due: e.target.value === 'none' ? { type: 'none' } : { type: 'after_run', days: 7, hours: 0, timeZone: browserZone() } as AssignNode['due'] })}>
          <option value="after_run">After the step runs</option>
          <option value="none">No due date</option>
        </select>
      </div>
      {due.type === 'after_run' && (
        <>
          <div className="field-row">
            <div className="field"><label htmlFor="n-afd-days">Days</label><input id="n-afd-days" className="input" type="number" min={0} max={365} value={due.days} data-testid="flow-assign-days"
              onChange={(e) => patch({ due: { ...due, days: Math.max(0, Math.min(365, Number(e.target.value) || 0)) } })} /></div>
            <div className="field"><label htmlFor="n-afd-hours">Hours</label><input id="n-afd-hours" className="input" type="number" min={0} max={8760} value={due.hours}
              onChange={(e) => patch({ due: { ...due, hours: Math.max(0, Math.min(8760, Number(e.target.value) || 0)) } })} /></div>
          </div>
          <div className="field"><label htmlFor="n-afd-zone">Time zone</label>
            <select id="n-afd-zone" className="select" value={zone} data-testid="flow-assign-zone" onChange={(e) => patch({ due: { ...due, timeZone: e.target.value } as AssignNode['due'] })}>
              {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
            </select>
            <span className="help">Due {due.days} day{due.days === 1 ? '' : 's'}{due.hours ? ` and ${due.hours} h` : ''} after the step runs; the supplier’s email shows it in this time zone.</span>
          </div>
        </>
      )}
      <div className="field"><label htmlFor="n-afd-notes">Notes to the supplier</label>
        <textarea id="n-afd-notes" className="textarea" rows={3} maxLength={2000} value={node.notes} placeholder="e.g. Please deliver {{asset.title}} in the house format." onChange={(e) => patch({ notes: e.target.value })} />
        <span className="help">Use {'{{asset.title}}'} or {'{{nodes.<step>.output.<field>}}'} to insert values.</span>
      </div>
      <div className="assign-items" data-testid="flow-assign-items">
        <div className="assign-items-head">
          <h3>Requested items</h3>
          <button type="button" className="btn ghost sm" disabled={node.items.length >= 30} data-testid="flow-assign-add-item"
            onClick={() => patch({ items: [...node.items, { kind: 'document', label: 'Document', description: '', acceptedTypes: [...ITEM_TYPE_PRESETS.document], required: true }] })}><Plus size={13} /> Add item</button>
        </div>
        <div className="assign-item media">
          <div className="assign-item-title"><strong>Media</strong><span className="pill accent">Always requested</span></div>
          <span className="faint">{spec ? `${spec.name}: ${typesText(spec.acceptedTypes)}` : 'Any media file PRISM can ingest'}</span>
        </div>
        {spec?.defaultItems.map((i, n) => (
          <div className="assign-item media" key={`spec-${n}`}>
            <div className="assign-item-title"><strong>{i.label}</strong><span className="pill">From the spec</span></div>
            <span className="faint">{i.description ? `${i.description} · ` : ''}{typesText(i.acceptedTypes)}{i.required ? '' : ' · optional'}</span>
          </div>
        ))}
        {node.items.map((it, n) => (
          <ItemRow key={n} item={it} n={n}
            onChange={(next) => patch({ items: node.items.map((x, j) => (j === n ? next : x)) })}
            onRemove={() => patch({ items: node.items.filter((_, j) => j !== n) })} />
        ))}
        {!node.items.length && !spec?.defaultItems.length && <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>Only the media. Add items for manuscripts, flyers, subtitles and so on.</p>}
      </div>
      <div className="field"><label htmlFor="n-afd-open">{groupScope ? 'If every placeholder already has an open assignment' : 'If the asset already has an open assignment'}</label>
        <select id="n-afd-open" className="select" value={node.ifOpen} data-testid="flow-assign-ifopen" onChange={(e) => patch({ ifOpen: e.target.value as AssignNode['ifOpen'] })}>
          <option value="fail">Fail the step</option>
          <option value="use_existing">Continue with that assignment</option>
        </select>
      </div>
      <span className="help">Later steps can use <span className="mono">{`{{nodes.${node.id}.output.assignmentId}}`}</span> and <span className="mono">{`{{nodes.${node.id}.output.dueAt}}`}</span>. Follow it with <b>Wait for delivery</b> to continue once Intake accepts the delivery.</span>
    </>
  );
}

/** NodeInspector.tsx: the fields of a Repeat until whose check is delivery_accepted (which Assign for delivery step). */
export function DeliveryCheckFields({ node, graph, set }: { node: RepeatNode; graph: AutomationGraph; set: (patch: Partial<AutomationNode>) => void }) {
  const check = node.check.type === 'delivery_accepted' ? node.check : { type: 'delivery_accepted' as const };
  const steps = graph.nodes.filter((n): n is AssignNode => n.type === 'assign_for_delivery');
  const waitsForAccepted = node.all.some((c) => c.path === `nodes.${node.id}.output.response.state` && c.op === 'equals' && c.value === 'accepted');
  return (
    <>
      <div className="field"><label htmlFor="n-dlv-from">Delivery</label>
        <select id="n-dlv-from" className="select" value={check.fromNode ?? ''} data-testid="flow-delivery-from"
          onChange={(e) => set({ check: e.target.value ? { type: 'delivery_accepted', fromNode: e.target.value } : { type: 'delivery_accepted' } } as Partial<AutomationNode>)}>
          <option value="">The asset’s newest assignment</option>
          {steps.map((s) => <option key={s.id} value={s.id}>{s.label ?? NODE_TYPES.assign_for_delivery.label} ({s.id})</option>)}
        </select>
        <span className="help" data-testid="flow-delivery-help">
          Each check reads the assignment’s state into <span className="mono">{`nodes.${node.id}.output.response`}</span> (<span className="mono">state</span>, <span className="mono">outcome</span>, <span className="mono">accepted</span>, <span className="mono">dueAt</span>).
          It leaves by <b>true</b> once Intake accepts the delivery (complete or with missing items), and by <b>false</b> after the last check. A cancelled or expired assignment fails the step at once.
        </span>
      </div>
      {!waitsForAccepted && (
        <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} data-testid="flow-delivery-accepted"
          onClick={() => set({ all: [acceptedCondition(node.id)] } as Partial<AutomationNode>)}>Wait until accepted</button>
      )}
    </>
  );
}

/** GraphCanvas.tsx: the node's line, or null when it is not a portal step. */
export function describePortalStep(n: AutomationNode): string | null {
  if (n.type === 'assign_for_delivery') {
    const who = `${n.scope === 'group' ? 'The run’s group, to' : 'To'} ${n.assignee.type === 'company' ? 'a company' : 'one supplier'}`;
    return n.due.type === 'after_run' ? `${who}, due in ${n.due.days} d${n.due.hours ? ` ${n.due.hours} h` : ''}` : who;
  }
  if (n.type === 'repeat_until' && n.check.type === 'delivery_accepted') return 'Wait for the delivery to be accepted';
  return null;
}
